import { SelectChangeEvent } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import TranslationField from "../../../type/translationField";
import LanguagePoint from "../../Global/LanguagePoint";
import SelectLanguage from "../../Global/SelectLanguage";
import Warning from "../../ErrorAndAlert/Warning";
import { ThemeProvider } from "@emotion/react";
import { theme } from "../../Register/AddRegister/FormTheme";
import ScientificName from "./ScientificName";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import {
  defaultLang,
  initialTranslationField,
  secondLang,
} from "../../../utils";

const AddScientificNameForm: React.FC<{
  send: (
    name: TranslationField,
    sName: { slug: string; scientific_name_transes: TranslationField }[]
  ) => void;
  sendResponse: any;
  getInfo?: any;
  textButton: string;
}> = ({ send, sendResponse, getInfo, textButton }) => {
  const { t } = useTranslation();
  const [lang, setLang] = useState<string>(defaultLang);
  const [name, setName] = useState<TranslationField>(initialTranslationField);
  const [scientificNames, setScientificNames] = useState<
    { slug: string; scientific_name_transes: TranslationField }[]
  >([{ slug: "", scientific_name_transes: initialTranslationField }]);
  const [loading, setLoading] = useState<boolean>(true);
  const [dirty, isDirty] = useState<boolean>(true);
  const [showError, isShowError] = useState<boolean>(false);
  const history = useHistory();
  const handleLangChange = (event: SelectChangeEvent) => {
    setLang(event.target.value.toLowerCase());
  };
  useEffect(() => {
    if (sendResponse) {
      toast.success(
        `${
          getInfo
            ? `${t("Message.The record updated successfully")}`
            : `${t("Message.The record added successfully")}`
        }`
      );
      history.replace("/setting/scientific-names");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendResponse]);

  useEffect(() => {
    if (getInfo) {
      setScientificNames([]);
      setName(getInfo.data.name_transes);
      setScientificNames(getInfo.data.scientific_names);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getInfo]);

  useEffect(() => {
    if (getInfo) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getInfo]);

  const nameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedName = { ...name };
    updatedName[lang] = event.target.value;
    setName(updatedName);
  };

  const scientificNameChange = (index: number, value: string) => {
    const updatedNames = scientificNames.map((sName, i) => {
      if (i === index) {
        return {
          ...sName,
          scientific_name_transes: {
            ...sName.scientific_name_transes,
            [lang]: value,
            [lang === secondLang ? defaultLang : secondLang]:
              sName.scientific_name_transes[
                lang === secondLang ? defaultLang : secondLang
              ],
          },
        };
      } else {
        return sName;
      }
    });

    setScientificNames(updatedNames);
  };

  const addScientificName = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let error = false;
    scientificNames.forEach((name) => {
      if (!name.scientific_name_transes[defaultLang]) {
        error = true;
      }
    });
    if (name[defaultLang] && !error) {
      isDirty(false);
      send(name, scientificNames);
    }
  };

  return (
    <Fragment>
      {loading && getInfo ? (
        <p className="text-center text-2xl font-medium text-default-text">
          {t("Message.Loading")}
        </p>
      ) : (
        <form onSubmit={addScientificName}>
          <ThemeProvider theme={theme}>
            <p className="text-sm text-default-text mb-[6px]">
              {t("AddPlanting.Language")}
            </p>
            <SelectLanguage
              handleLangChange={handleLangChange}
              lang={lang.toUpperCase()}
            />
          </ThemeProvider>
          <p className="text-sm text-default-text mb-[6px] mt-7">
            {t("Tree Species.Name")} *{" "}
            <LanguagePoint lang={lang.toUpperCase()} />
          </p>
          <input
            onChange={nameChange}
            value={lang === secondLang ? name[secondLang] : name[defaultLang]}
            type="text"
            className="input-field pl-4 py-3 text-sm placeholder-ph-light-gray"
            placeholder={t("Tree Species.Name") + ""}
          />
          {showError && !name[defaultLang] && (
            <Fragment>
              <p className="error-text">{t("Message.Required field")}</p>
            </Fragment>
          )}

          <div className="relative">
            {scientificNames.map((name, index: number) => (
              <Fragment key={index}>
                <p className="text-sm text-default-text mb-[6px] mt-7">
                  {t("Tree Species.Scientific name")} *{" "}
                  <LanguagePoint lang={lang.toUpperCase()} />
                </p>
                <ScientificName
                  index={index}
                  sName={
                    lang === secondLang
                      ? name.scientific_name_transes[secondLang]
                      : name.scientific_name_transes[defaultLang]
                  }
                  key={index}
                  sNameChange={scientificNameChange}
                />
                {showError && !name.scientific_name_transes[defaultLang] && (
                  <p className="error-text">{t("Message.Required field")}</p>
                )}
              </Fragment>
            ))}
            {scientificNames.length > 1 && (
              <button
                onClick={() => {
                  setScientificNames(
                    scientificNames.filter(
                      (a, index: number) => index !== scientificNames.length - 1
                    )
                  );
                }}
                type="button"
                className="text-sm text-ph-red-warning mt-3 font-medium absolute right-0"
              >
                {t("PlantingFilter.Delete")}
              </button>
            )}

            <br />
            <button
              onClick={() => {
                setScientificNames([
                  ...scientificNames,
                  {
                    slug: "",
                    scientific_name_transes: initialTranslationField,
                  },
                ]);
              }}
              type="button"
              className="text-sm text-ph-btn font-semibold"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              {t("Scientific name.Add another scientific name")}
            </button>
          </div>
          <button
            onClick={() => {
              isShowError(true);
            }}
            type="submit"
            className="green-btn text-sm  px-6 py-2 mt-5 float-right font-medium"
          >
            {textButton}
          </button>
        </form>
      )}
      <Warning when={dirty} onCancel={() => false} onOK={() => true} />
    </Fragment>
  );
};

export default AddScientificNameForm;
